import { ChangeEvent, Component, Ref, TextareaHTMLAttributes } from 'react';
import cn from 'classnames';
import styles from './input.scss';
import Icon from 'components/Shared/Icon';
import { ToolTip } from 'components/Shared/ToolTip';

export type OnTextAreaChangeHandler = (
  e: ChangeEvent<HTMLTextAreaElement>,
  newValue?: string
) => void;

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  innerRef?: Ref<HTMLTextAreaElement>;
  invalid?: boolean;
  valid?: boolean;
  className?: string;
  unit?: string;
  onChange: OnTextAreaChangeHandler;
  rows?: number;
  classNames?: {
    inputField?: string;
  };
  textlimit?: number;
}

const CHAR_SIZE = 7;
const PADDING = 17;
const DEFAULT_NUM_ROWS = 3;
export class TextArea extends Component<TextAreaProps> {
  public render() {
    const {
      innerRef,
      invalid,
      valid,
      className,
      onChange,
      rows,
      classNames = {},
      ...rest
    } = this.props;

    const unit = Boolean(this.props.unit && this.props.value) && (
      <span className={styles.unit} style={{ left: this.leftPad() }}>
        {this.props.unit}
      </span>
    );

    const numRows = rows || DEFAULT_NUM_ROWS;

    return (
      <div
        className={cn(styles.input, className, {
          [styles.invalid]: invalid,
          [styles.valid]: valid
        })}
      >
        <textarea
          ref={innerRef}
          className={cn(styles.inputField, classNames.inputField, {
            [styles.invalid]: invalid,
            [styles.valid]: valid
          })}
          onChange={this.handleChange}
          rows={numRows}
          {...rest}
        />
        {invalid && (
          <Icon icon="icon-warning" inline className={cn(styles.inputIcon, styles.invalid)} />
        )}
        {valid && (
          <Icon
            icon="icon-check"
            inline
            size="small"
            className={cn(styles.inputIcon, styles.valid)}
          />
        )}
        {unit}
      </div>
    );
  }

  private handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { textlimit = 0 } = this.props;
    const newValue = e.currentTarget.value;

    this.props.onChange(e, textlimit ? newValue.substring(0, textlimit) : newValue);
  };

  private leftPad = () => {
    const value = this.props.value || '';
    return `${PADDING + CHAR_SIZE * (typeof value !== 'number' ? value.length : value)}px`;
  };
}

interface Props extends TextAreaProps {
  label?: string;
  tooltip?: string;
}

export const LabeledTextArea = ({ className, label, tooltip, ...input }: Props) => (
  <div className={cn(styles.labelInput, className)}>
    {(label || tooltip) && (
      <span>
        {label && <label>{label}</label>}
        {tooltip && <ToolTip className={styles.tooltip} text={tooltip} />}
      </span>
    )}
    <TextArea {...input} />
  </div>
);
